import Button from "@mui/material/Button"
import { Grid2 as Grid } from "@mui/material"
import { Formik, Field, useFormikContext } from "formik"
import { Supporto } from "../../types"
import { useEffect, useMemo } from "react"
import * as yup from "yup"
import { transformErrorForForm } from "../../utils/utils"
import { FormikSelectVocabolario, FormikTextField, SelectFormato } from "./formsHelpers"
import { FormInDialogEditProps } from "../DialogForm"
import { useTranslation } from "react-i18next"
import { useEstimatedDuration } from "../../hooks/supporti"
import { EdgeCodesField } from "./EdgeCodesField"

function DurationCalc() {
  const { values, setFieldValue } = useFormikContext<Supporto>()
  const { mutateAsync: estimatedDuration } = useEstimatedDuration()

  useEffect(() => {
    estimatedDuration({
      lunghezza: values.lunghezza,
      formato: values.formato,
      framerate: values.framerate,
    })
      .then((res) => {
        setFieldValue("durata_presunta", res.duration)
      })
      .catch((e) => {
        console.error("Error in estimatedDuration", e)
      })
  }, [values.lunghezza, values.formato, values.framerate])

  return null
}

export default function SupportoForm({ item, save, ref }: FormInDialogEditProps<Supporto>) {
  const { t } = useTranslation()

  const schema = useMemo(
    () =>
      yup.object().shape({
        descr_contenintore: yup.string(),
        edge_codes_data: yup.array().of(yup.object().shape({
          edge_code: yup.string(),
          sistema_colore: yup.number().required(),
          cromatismo: yup.number(),
        }))
      }),
    [t]
  )

  return (
    <Formik
      validationSchema={schema}
      initialValues={
        item || {
          formato: "",
          framerate: "",
          tipologia_materiale: "",
          tipo_supporto: "",
          base_supporto: "",
          status_archivio: "",
          audio: "",
          tipologia_colonna: "",
          tipologia_sonoro: "",
          durata_presunta: "",
          cromatismo: "",
          sist_colore: "",
        }
      }
      enableReinitialize
      onSubmit={(values, formikBag) => {
        return save(values as Supporto).catch((e) => {
          const localError = transformErrorForForm(e)
          formikBag.setErrors(localError)
        })
      }}
    >
      {({ handleSubmit, values, setFieldValue }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <DurationCalc />
            <Grid container spacing={1} sx={{ py: 2 }}>
              <Grid size={6}>
                <SelectFormato
                  label={t("supporto.formato")}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  value={values.formato ? values.formato.toString() : ""}
                  onChange={(e, fmt) => {
                    const value = e.target.value
                    console.log("e.target", fmt)
                    setFieldValue("formato", value)
                    setFieldValue("unit_mis", fmt?.unita_misura)
                  }}
                />
              </Grid>
              <Grid size={4}>
                <Field
                  component={FormikTextField}
                  type="number"
                  name="lunghezza"
                  label={t("supporto.lunghezza")}
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  helperText={t("supporto.lunghezza_helper")}
                />
              </Grid>
              <Grid size={2}>
                <Field
                  component={FormikTextField}
                  name="unit_mis"
                  label={t("supporto.unit_mis")}
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  disabled
                  helperText={t("supporto.unit_mis_helper")}
                />
              </Grid>
              <Grid size={6}>
                <Field
                  name="framerate"
                  label={t("supporto.framerate")}
                  component={FormikSelectVocabolario}
                  vocabolario="framerate"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <Field
                  name="durata_presunta"
                  label={t("supporto.durata_presunta")}
                  component={FormikTextField}
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid size={6}>
                <Field
                  name="tipologia_materiale"
                  label={t("supporto.tipologia_materiale")}
                  component={FormikSelectVocabolario}
                  vocabolario="materialilavorazione"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <Field
                  name="tipo_supporto"
                  label={t("supporto.tipo_supporto")}
                  component={FormikSelectVocabolario}
                  vocabolario="object_tipologis"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="base_supporto"
                  label={t("supporto.base_supporto")}
                  component={FormikSelectVocabolario}
                  vocabolario="base_supporto"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="status_archivio"
                  label={t("supporto.status_archivio")}
                  component={FormikSelectVocabolario}
                  vocabolario="statusarchiviopellicola"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="audio"
                  label={t("supporto.audio")}
                  component={FormikSelectVocabolario}
                  vocabolario="sonoro"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid size={6}>
                <Field
                  name="tipologia_colonna"
                  label={t("supporto.tipologia_colonna")}
                  component={FormikSelectVocabolario}
                  vocabolario="presenza_audio"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <Field
                  name="tipologia_sonoro"
                  label={t("supporto.tipologia_sonoro")}
                  component={FormikSelectVocabolario}
                  vocabolario="tecnica_suono"
                  margin="dense"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid size={6}></Grid>

              <Grid size={12}>
                <EdgeCodesField name="edge_codes_data" label={t("supporto.edge_codes")} />
              </Grid>

            </Grid>

            <Button ref={ref} type="submit" color="primary" sx={{ display: "none" }}></Button>
          </form>
        )
      }}
    </Formik>
  )
}
